import React from 'react';
import { Circle, G, Line, Path, Polygon, Rect, Text as SvgText } from 'react-native-svg';
import { DiagramCanvas } from '../DiagramCanvas';
import { FormulaTag } from '../FormulaTag';
import { LeaderLine } from '../LeaderLine';
import { DT } from '../tokens';

/** Psychrometric chart with state point + projections to DB / WB / DP / W / h. */
export function DiagramDewPointEnthalpy() {
  // State point coordinates
  const sx = 220, sy = 110;
  // Dew-point on saturation curve (same humidity, lower temp)
  const dpx = 130, dpy = 192;
  // Wet-bulb intersection on saturation
  const wbx = 168, wby = 175;

  return (
    <DiagramCanvas>
      {/* Axes */}
      <Line x1={40} y1={210} x2={325} y2={210} stroke={DT.dim} strokeWidth="1.4" />
      <Line x1={40} y1={28} x2={40} y2={210} stroke={DT.dim} strokeWidth="1.4" />
      <SvgText x={180} y={228} fill={DT.dim} fontSize="9" fontFamily={DT.mono} textAnchor="middle">Dry-bulb (°F) →</SvgText>
      <SvgText x={28} y={120} fill={DT.dim} fontSize="9" fontFamily={DT.mono} textAnchor="middle"
        rotation="-90" originX="28" originY="120">W (lb/lb) ↑</SvgText>

      {/* Saturation curve (100% RH) */}
      <Path d="M50,205 Q90,200 130,192 Q180,170 220,135 Q260,95 300,45"
        fill="none" stroke={DT.stroke} strokeWidth="2" />
      <SvgText x={296} y={42} fill={DT.desc} fontSize="8" fontFamily={DT.mono} textAnchor="end">100% RH</SvgText>

      {/* RH curves */}
      <Path d="M50,208 Q120,206 190,200 Q250,194 305,188" fill="none" stroke={DT.dim} strokeWidth="1" strokeDasharray="3,3" />
      <SvgText x={307} y={189} fill={DT.dim} fontSize="7" fontFamily={DT.mono}>30%</SvgText>
      <Path d="M50,207 Q110,202 170,190 Q230,175 290,155" fill="none" stroke={DT.dim} strokeWidth="1" strokeDasharray="3,3" />
      <SvgText x={292} y={156} fill={DT.dim} fontSize="7" fontFamily={DT.mono}>60%</SvgText>

      {/* Enthalpy lines (parallel diagonals) */}
      {[0.6, 0.95, 1.3].map((t, i) => (
        <Line key={i} x1={50 + t * 80} y1={210} x2={50 + t * 80 - 90} y2={50}
          stroke={DT.leader} strokeWidth="0.7" strokeDasharray="2,4" opacity={0.5} />
      ))}
      <SvgText x={75} y={48} fill={DT.leader} fontSize="7" fontFamily={DT.mono}>h ↗ (BTU/lb)</SvgText>

      {/* State point */}
      <Circle cx={sx} cy={sy} r={5.5} fill={DT.varColor} stroke={DT.stroke} strokeWidth="1.2" />

      {/* DB projection (down) */}
      <Line x1={sx} y1={sy} x2={sx} y2={210} stroke={DT.dim} strokeWidth="1" strokeDasharray="3,2" />
      <Circle cx={sx} cy={210} r={2.5} fill={DT.varColor} />

      {/* W (humidity ratio) projection (left) */}
      <Line x1={sx} y1={sy} x2={40} y2={sy} stroke={DT.dim} strokeWidth="1" strokeDasharray="3,2" />
      <Circle cx={40} cy={sy} r={2.5} fill={DT.varColor} />

      {/* DP — horizontal to saturation, then down */}
      <Line x1={sx} y1={sy} x2={dpx} y2={dpy} stroke="#7AAABB" strokeWidth="1" strokeDasharray="3,2" opacity={0.8} />
      <Circle cx={dpx} cy={dpy} r={3} fill="#7AAABB" />
      <Line x1={dpx} y1={dpy} x2={dpx} y2={210} stroke="#7AAABB" strokeWidth="1" strokeDasharray="3,2" opacity={0.6} />

      {/* WB — line up the wet-bulb slope to saturation */}
      <Line x1={sx} y1={sy} x2={wbx} y2={wby} stroke={DT.varColor} strokeWidth="1" strokeDasharray="3,2" opacity={0.8} />
      <Circle cx={wbx} cy={wby} r={3} fill={DT.varColor} />

      {/* h projection (perpendicular to enthalpy lines, to chart edge) */}
      <Line x1={sx} y1={sy} x2={290} y2={50} stroke={DT.leader} strokeWidth="0.8" strokeDasharray="2,3" opacity={0.6} />

      {/* Labels */}
      <LeaderLine x1={sx} y1={210} x2={250} y2={228} varName="DB" desc="Dry-bulb" anchor="start" />
      <LeaderLine x1={dpx} y1={210} x2={75} y2={228} varName="DP" desc="Dew point" anchor="end" />
      <LeaderLine x1={wbx} y1={wby} x2={120} y2={150} varName="WB" desc="Wet-bulb" anchor="end" />
      <LeaderLine x1={40} y1={sy} x2={20} y2={70} varName="W" desc="Humidity ratio" anchor="start" />
      <LeaderLine x1={290} y1={50} x2={300} y2={210} varName="h" desc="Enthalpy" anchor="end" />
      <LeaderLine x1={sx} y1={sy} x2={sx + 30} y2={sy - 25} varName="state" desc="point" anchor="start" />

      <FormulaTag formula="State point: DB, WB, DP, h, RH, W   (read 4, derive rest)" />
    </DiagramCanvas>
  );
}
